import { ChangeDetectionStrategy, Component, inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgbDropdownModule, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '@ortho-next/auth-core';
import { SseService } from '@ortho-next/sse';
import { map, Subscription } from 'rxjs';
import { APP_CONFIG, Plan, ToastService } from '../../core';
import { BaseHelpService, CaseService, Guide, GuideService, HelpService, ModelService, UserService } from '../../services';
import { AnatomicalSideLabelPipe, BaseComponent, BoneTypeLabelPipe, ClosureModalComponent, ConfirmationComponent, HelpComponent, PlanTypeLabelPipe } from '../../shared';


/**
 * Horizontal bar on top
 */
@Component({
	selector: 'navbar',
	templateUrl: './navbar.component.html',
	imports: [
		NgbDropdownModule,
		BoneTypeLabelPipe,
		PlanTypeLabelPipe,
		AnatomicalSideLabelPipe
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class NavbarComponent extends BaseComponent implements OnInit, OnDestroy {

	private readonly sse = inject(SseService);
	private readonly config = inject(APP_CONFIG);
	private readonly injector = inject(Injector);
	private readonly modalSrv = inject(NgbModal);
	private readonly userSrv = inject(UserService);
	private readonly caseSrv = inject(CaseService);
	private readonly authSrv = inject(AuthService);
	private readonly modelSrv = inject(ModelService);
	private readonly toastSrv = inject(ToastService);
	private readonly guideSrv = inject(GuideService);


	currentCase?: Plan;
	guideList: Guide[] = [];
	isReadOnly = toSignal(this.caseSrv.isReadOnly());
	username = toSignal(this.userSrv.getUserName().pipe(map(name => `${name.firstName} ${name.lastName}`)), { initialValue: '' });

	private resetKey?: string;
	private caseSub$?: Subscription;
	private readonly isPltDemo = toSignal(this.userSrv.isPlatformDemo(), { initialValue: false });
	private readonly isJPSDemo = toSignal(this.userSrv.isJPSDemoProduct(), { initialValue: false });

	readonly envName = this.config.envName;
	readonly eifuSiteUrl = this.config.eifuSite;
	readonly buildVersion = this.config.buildVer;


	ngOnInit(): void {
		this.caseSub$ = this.caseSrv.getCurrentCase().subscribe(c => {
			this.currentCase = c;
			this.handlePlanResetting();
		});
	}

	/**
	 * Watermark Demo.
	 */
	get isWatermarkVisible(): boolean {
		return this.stateType === this.stateTypeEnum.templating ? this.isJPSDemo() : this.isPltDemo();
	}

	/**
	 * Check if save button is disabled.
	 */
	get isSaveDisabled(): boolean {
		return this.isReadOnly() || this.isMechAxisWorkflow;
	}

	/**
	* Get plan step label.
	*/
	get planStepLabel(): string {
		if (!this.currentCase) return '';
		return this.currentCase.isPostOperative ? this.labels().NAVBAR_COMPONENT_PLAN_STEP_POSTOP : this.labels().NAVBAR_COMPONENT_PLAN_STEP_PREOP;
	}

	/**
	* Open about page.
	*/
	openAboutPage(): void {
		window.open(`${this.config.aboutSite}${this.langSrv.currentLang}`, '_blank');
	}

	/**
	* Open legal terms page.
	*/
	openLegalTerms(): void {
		this.userSrv.getUserCountry().subscribe(code => {
			window.open(`${this.config.legalTermsSite}?countryCode=${code}`);
		});
	}

	/**
	* Logout.
	*/
	logout(): void {
		this.authSrv.singleSignOut();
	}

	/**
	 * Persist model scene.
	 */
	save(): void {
		if (!this.isSaveDisabled) {
			const currentCase = this.canvasSrv.currentCase!;
			this.modelSrv.persistModel(currentCase.id, currentCase.userGuid).subscribe({
				next: () => this.toastSrv.showSuccess(this.labels().NAVBAR_COMPONENT_PLAN_SAVED_SUCCESS),
				error: () => this.toastSrv.showError(this.labels().NAVBAR_COMPONENT_PLAN_SAVED_ERROR)
			});
		}
	}

	/**
	 * Get Guide list when open dropdown the first time.
	 */
	getGuideList(open: boolean): void {
		if (!!open && (!this.guideList || this.guideList.length === 0)) {
			this.guideSrv.getGuideList().subscribe(list => this.guideList = list);
		}
	}

	/**
	 * Open guide page.
	 */
	openGuide(path: string): void {
		const guideUrl = this.guideSrv.getGuideUrl(path);
		window.open(guideUrl, '_blank');
	}

	/**
	* Open modal of help info.
	*/
	openHelpModal(): void {
		if (!this.modalSrv.hasOpenModals()) {
			this.modalSrv.open(HelpComponent, {
				backdropClass: 'wrapper-modal-help', windowClass: 'wrapper-modal-help', injector:
					Injector.create({
						parent: this.injector,
						providers: [{ provide: BaseHelpService, useClass: HelpService }]
					})
			});
		}
	}

	/**
	* Open modal of confirmation before exit.
	*/
	openClosingModal(): void {
		if (!this.modalSrv.hasOpenModals()) {
			this.modalSrv.open(ClosureModalComponent, { centered: true, backdrop: 'static' });
		}
	}

	/**
	 * Subscribe to analysis resetting event.
	 */
	private handlePlanResetting(): void {
		if (!this.resetKey) {
			const caseGuid = this.currentCase!.id;
			this.resetKey = `OrthoNext.Ray.Reset.${caseGuid}`;
			setTimeout(() => {
				this.sse.subscribe(this.resetKey!, (success: boolean) => {
					if (success) {
						this.modalSrv.dismissAll();
						this.openPlanChangedWarning(caseGuid);
					}
				});
			}, 5000);
		}
	}

	private openPlanChangedWarning(caseGuid: string): void {
		const modalRef: NgbModalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
		(modalRef.componentInstance as ConfirmationComponent).config = {
			title: this.labels().INVALID_PLAN_MODAL_TITLE,
			message: this.labels().PLAN_CHANGED_WARNING_MESSAGE,
			confirmBtn: this.labels().INVALID_PLAN_MODAL_CONFIRM_BTN,
			isCancelBtn: false
		};
		modalRef.result.then(() => globalThis.location.href = `${this.config.planDetailsSite}${caseGuid}`).catch(err => false);
	}


	ngOnDestroy(): void {
		this.caseSub$?.unsubscribe();
		this.resetKey && this.sse.unsubscribe(this.resetKey);
	}

}
